import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useStore } from 'hooks/useRootStore';
import { emailPattern, phonePattern } from 'utils/patterns';
import { ControlledInput } from 'components/Inputs/ControlledInput/ControlledInput';
import { TextAreaInput } from 'components/Inputs/TextAreaInput';
import SelectComponent from 'components/Inputs/SelectComponent/SelectComponent';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import { MapBox } from 'components/Core/MapBox/MapBox';
import { TableActionsButtons } from 'components/RequestsComponents/TableActionsButtons';
import { ISelectStyleProps } from 'components/Inputs/SelectComponent/types';
import RequestHeader from 'components/RequestsComponents/RequestHeader';
import AutocompleteSelect from 'components/Inputs/AutocompleteSelect/AutocompleteSelect';
import EntityOption from 'components/Core/EntityOption';
import PointCategoryItem from 'components/Core/PointCategoryItem';
import { IPointRequestData } from 'stores/requests/types';
import { IBrandRef } from 'types/common';
import { IPointsCategoryData } from 'stores/points/types';
import { pointRequestFormTranslations } from './translations';
import { PointRequestFormTransFields } from './types';
import styles from './pointRequestForm.module.scss';

const selectStyles: ISelectStyleProps = {
  controlStyles: {
    border: '1px solid red',
  },
};

export const PointRequestForm: FC = observer(() => {
  const {
    requestsStore: { pointRequestData },
  } = useStore();

  const { t } = useMyTranslations<PointRequestFormTransFields>([pointRequestFormTranslations.name]);

  const useFormMethods = useForm<IPointRequestData>({
    defaultValues: pointRequestData || undefined,
  });

  const renderCategoriesOption = useCallback((data: IPointsCategoryData) => {
    return <PointCategoryItem key={data.id} category={data} />;
  }, []);

  // Inside react-select's own chip the pill would be a box in a box, so the
  // chip content is the plain image + name, same as PointBasicForm.
  const renderCategoriesValue = useCallback((data: IPointsCategoryData) => {
    return <EntityOption image={data.categoryImage} entityType='points-categories' name={data.categoryName} />;
  }, []);

  const renderBrandOption = useCallback((data: IBrandRef) => {
    return <EntityOption key={data.id} image={data.logoUrl} entityType='brands' name={data.name} />;
  }, []);

  return pointRequestData ? (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <RequestHeader>
        <TableActionsButtons id={pointRequestData.id} entity='points' status={pointRequestData.status} />
      </RequestHeader>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.formSection}`}>
            <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('title')}</h6>
            <ControlledInput
              name='name'
              type='text'
              label={t('name')}
              placeholder=''
              disabled
              classNameProp={pointRequestData.changedFieldsNames.includes('name') ? styles.highlight : undefined}
            />
            <TextAreaInput
              name='description'
              label={t('description')}
              placeholder={t('description')}
              disabled
              classNameProp={pointRequestData.changedFieldsNames.includes('description') ? styles.highlight : undefined}
            />
            <ControlledInput
              name='phone'
              type='text'
              label={t('phone')}
              placeholder={t('phone')}
              registerPattern={phonePattern}
              disabled
            />
            <ControlledInput
              name='email'
              type='text'
              label={t('email')}
              placeholder={t('email')}
              registerPattern={emailPattern}
              disabled
            />
            <AutocompleteSelect
              name='brand'
              label={t('brand')}
              placeholder=''
              data={[]}
              customSingleValue={renderBrandOption}
              isDisabled
            />
            <SelectComponent
              name='categories'
              label={t('categories')}
              placeholder=''
              options={[]}
              optionLabel='categoryName'
              optionValue='id'
              customOption={renderCategoriesOption}
              customMultiValue={renderCategoriesValue}
              isDisabled
              isMulti
              styleProps={pointRequestData.changedFieldsNames.includes('categories') ? selectStyles : undefined}
            />
            <ControlledInput name='address' type='text' label={t('address')} placeholder={t('address')} disabled />
            <div className={styles.locationBlock}>
              <ControlledInput name='latitude' type='number' label={t('latitude')} placeholder='' disabled />
              <ControlledInput
                name='longitude'
                type='number'
                label={t('longitude')}
                placeholder=''
                isRemoveMargin
                disabled
              />
            </div>
          </section>
          <section className={`${styles.rightSideSection}`}>
            <ImageUploader
              name='image'
              title={t('image')}
              path='points'
              disabled
              classNameProp={pointRequestData.changedFieldsNames.includes('image') ? styles.highlight : undefined}
            />
            {location && (
              <MapBox
                locations={[{ lat: +pointRequestData.latitude, lng: +pointRequestData.longitude }]}
                draggableMarkers={false}
                classNameProp={pointRequestData.changedFieldsNames.includes('address') ? styles.highlight : undefined}
              />
            )}
          </section>
        </form>
      </div>
    </FormProvider>
  ) : (
    <p>No such point was found</p>
  );
});
